import { useRecommendedSortOrderExperimentation } from 'Services/optimizely/experimentations/useRecommendedSortOrderExperimentation';
import { useResetTimeId } from 'Stores/BidCountSortStore/BidCountSortStore';
import { LIST_POSITION_PAGES } from 'Stores/VehicleListPositionStore/VehicleListPositionsStore.consts';
import type { Filter, VehicleListFilterQuery } from 'Types/filter';
import type { Pagination } from 'Types/pagination';
import type { VehicleListItem } from 'Types/vehicle';
import useVehicleListFilterQuery from 'Utilities/hooks/query/useVehicleListFilterQuery';

import useFetchVehicleList from '../useFetchVehicleList/useFetchVehicleList';
import useFormatVehicleListData from '../useFormatVehicleListData/useFormatVehicleListData';
import { useMakeModelCountUpdater } from '../useMakeModelCountUpdater';
import { useOnFiltersChanged } from '../useOnFiltersChanged/useOnFiltersChanged';
import useRefreshVehicleList from '../useRefreshVehicleList/useRefreshVehicleList';
import useSelectedVehicle from '../useSelectedVehicle/useSelectedVehicle';
import { useUnshortlistVehicles } from '../useUnshortlistVehicles/useUnshortlistVehicles';
import { useVehicleListCache } from '../useVehicleListCache/useVehicleListCache';
import useVehicleListCount from '../useVehicleListCount/useVehicleListCount';
import useVehicleListCurrentPage from '../useVehicleListCurrentPage/useVehicleListCurrentPage';
import useVehicleListPagination from '../useVehicleListPagination/useVehicleListPagination';
import useVehicleListPosition from '../useVehicleListPosition/useVehicleListPosition';
import useVehicleListQuery from '../useVehicleListQuery/useVehicleListQuery';

interface UseVehicleListWrapperParams {
	filters: Filter;
	query: VehicleListFilterQuery;
}

interface VehicleListWrapper {
	isError: boolean;
	isLoading: boolean;
	onFetchNextPage: () => void;
	onRefresh: () => void;
	pagination: Pagination;
	vehicleCount: number;
	vehicles: VehicleListItem[];
}

const useVehicleListWrapper = ({ filters, query }: UseVehicleListWrapperParams): VehicleListWrapper => {
	// TODO: Refactor `useVehicleListFilterQuery` as it is causing several rerenders in the app.
	const filterQuery = useVehicleListFilterQuery(query);

	const resetTimeId = useResetTimeId();
	useOnFiltersChanged(resetTimeId);

	const { recommendedSortOrderVersion, shouldIncludeRecommendedSortOrderVersion } =
		useRecommendedSortOrderExperimentation();

	const { asUrlPath, displayUrl, serverQuery } = filterQuery || {};
	const vehicleListQuery = useVehicleListQuery({
		recommendedSortOrderVersion,
		serverQuery,
		shouldIncludeRecommendedSortOrderVersion,
	});
	const {
		data: vehiclesData,
		fetchNextPage,
		hasNextPage,
		isError = false,
		isLoading,
		refetch,
	} = useFetchVehicleList({
		query: vehicleListQuery,
	});

	useMakeModelCountUpdater({ filters, serverQuery });
	useVehicleListPosition(isLoading, LIST_POSITION_PAGES.marketplace);
	useVehicleListCache(LIST_POSITION_PAGES.marketplace);

	const selectedVehicle = useSelectedVehicle(filters.selectedVehicle);
	const vehicles = useFormatVehicleListData({ selectedVehicle, vehicles: vehiclesData });
	const vehicleCount = useVehicleListCount({ selectedVehicle, vehicles: vehiclesData });
	const currentPage = useVehicleListCurrentPage(vehiclesData);
	const pagination = useVehicleListPagination({ currentPage, hasNextPage: Boolean(hasNextPage) });

	const onRefresh = useRefreshVehicleList({ asUrlPath, displayUrl, isReplaceVehicleListUrl: true, refetch });
	useUnshortlistVehicles(onRefresh);

	return {
		isError,
		isLoading,
		onFetchNextPage: fetchNextPage,
		onRefresh,
		pagination,
		vehicleCount,
		vehicles,
	};
};

export default useVehicleListWrapper;
